/**
 * Uptime SLA Monitoring Service
 * Tracks system availability and SLA compliance
 * SOC2 Compliance: CC7 - Availability
 * Phase 1: Safe additive implementation - no workflow disruption
 */

import { getFailoverAwareDatabase } from '../config/firebaseWithFailover';
import { collection, addDoc, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import Logger from './logger';
import systemHealthService from './systemHealthService';

export interface UptimeRecord {
  id?: string;
  timestamp: Date;
  status: 'up' | 'down' | 'degraded';
  healthScore: number;
  duration: number; // milliseconds
  incidentId?: string;
  details?: Record<string, any>;
}

export interface SLAMetrics {
  targetUptime: number; // percentage (e.g., 99.9)
  currentUptime: number; // percentage
  totalTime: number; // milliseconds
  uptime: number; // milliseconds
  downtime: number; // milliseconds
  degradedTime: number; // milliseconds
  incidents: number;
  mttr: number; // Mean Time To Recovery (milliseconds)
  lastIncident?: Date;
  complianceStatus: 'compliant' | 'at_risk' | 'non_compliant';
  periodStart: Date;
  periodEnd: Date;
}

export interface SLAMonitoringConfig {
  enabled: boolean;
  targetUptime: number; // 99.9 = 99.9%
  collectionName: string;
  checkInterval: number; // milliseconds
  retentionDays: number;
  alertThreshold: number; // Alert if uptime drops below this
}

class UptimeSLAMonitoringService {
  private static instance: UptimeSLAMonitoringService;
  private config: Required<SLAMonitoringConfig>;
  private monitoringInterval: NodeJS.Timeout | null = null;
  private isMonitoring: boolean = false;
  private currentStatus: 'up' | 'down' | 'degraded' = 'up';
  private lastCheckTime: number = Date.now();
  private uptimeRecords: UptimeRecord[] = [];
  private currentIncidentId: string | null = null;
  private incidentStartTime: number | null = null;

  private constructor(config: Partial<SLAMonitoringConfig> = {}) {
    this.config = {
      enabled: config.enabled ?? true,
      targetUptime: config.targetUptime ?? 99.9,
      collectionName: config.collectionName ?? 'uptimeRecords',
      checkInterval: config.checkInterval ?? 60000, // 1 minute
      retentionDays: config.retentionDays ?? 365, // 1 year
      alertThreshold: config.alertThreshold ?? 99.5
    };

    // Don't start monitoring in constructor - wait for explicit updateConfig() call
    // This prevents Firebase access during build/prerender
  }

  /**
   * Get singleton instance
   */
  public static getInstance(config?: Partial<SLAMonitoringConfig>): UptimeSLAMonitoringService {
    if (!UptimeSLAMonitoringService.instance) {
      UptimeSLAMonitoringService.instance = new UptimeSLAMonitoringService(config);
    }
    return UptimeSLAMonitoringService.instance;
  }

  /**
   * Start monitoring
   */
  private startMonitoring(): void {
    if (this.isMonitoring) return;

    this.isMonitoring = true;
    this.lastCheckTime = Date.now();

    // Perform initial check
    this.performUptimeCheck();

    // Set up periodic checks
    this.monitoringInterval = setInterval(() => {
      this.performUptimeCheck();
    }, this.config.checkInterval);

    Logger.info('Uptime SLA Monitoring Service started', {
      checkInterval: this.config.checkInterval,
      targetUptime: this.config.targetUptime
    });
  }

  /**
   * Perform uptime check
   */
  private async performUptimeCheck(): Promise<void> {
    try {
      const now = Date.now();
      const healthSummary = await systemHealthService.getSystemHealth();
      const healthScore = healthSummary.healthScore;
      const overallStatus = healthSummary.overallStatus;

      // Determine status based on health
      let status: 'up' | 'down' | 'degraded';
      if (overallStatus === 'unhealthy' || healthScore < 50) {
        status = 'down';
      } else if (overallStatus === 'degraded' || healthScore < 80) {
        status = 'degraded';
      } else {
        status = 'up';
      }

      // Calculate duration since last check
      const duration = now - this.lastCheckTime;

      // Record uptime status
      const record: UptimeRecord = {
        timestamp: new Date(now),
        status,
        healthScore,
        duration,
        details: {
          overallStatus,
          alerts: healthSummary.alerts,
          services: healthSummary.services.length,
          unhealthyServices: healthSummary.services.filter(s => s.status === 'unhealthy').length
        }
      };

      // Handle incident tracking
      if (status === 'down' && this.currentStatus !== 'down') {
        // New incident started
        this.currentIncidentId = `incident_${now}`;
        this.incidentStartTime = now;
        record.incidentId = this.currentIncidentId;
        Logger.warn('Uptime incident detected', { incidentId: this.currentIncidentId, healthScore });
      } else if (status !== 'down' && this.currentStatus === 'down') {
        // Incident resolved
        if (this.incidentStartTime) {
          const incidentDuration = now - this.incidentStartTime;
          Logger.info('Uptime incident resolved', {
            incidentId: this.currentIncidentId,
            duration: incidentDuration,
            mttr: incidentDuration
          });
          this.currentIncidentId = null;
          this.incidentStartTime = null;
        }
      }

      // Store record locally
      this.uptimeRecords.push(record);
      if (this.uptimeRecords.length > 1000) {
        this.uptimeRecords = this.uptimeRecords.slice(-1000);
      }

      // Store in Firestore (async, don't block)
      this.storeUptimeRecord(record).catch(error => {
        Logger.error('Failed to store uptime record', error instanceof Error ? error : new Error('Unknown error'));
      });

      // Update current status
      this.currentStatus = status;
      this.lastCheckTime = now;

    } catch (error) {
      Logger.error('Uptime check failed', error instanceof Error ? error : new Error('Unknown error'));
      // Mark as down if check fails
      this.currentStatus = 'down';
    }
  }

  /**
   * Store uptime record in Firestore
   */
  private async storeUptimeRecord(record: UptimeRecord): Promise<void> {
    try {
      // Skip during prerender/SSR (Firebase not initialized)
      if (typeof window === 'undefined' || typeof navigator === 'undefined' || 
          /HeadlessChrome/i.test(navigator?.userAgent || '')) {
        return;
      }

      const db = getFailoverAwareDatabase();
      if (!db) {
        Logger.warn('Database not available for uptime record storage');
        return;
      }

      await addDoc(collection(db, this.config.collectionName), {
        ...record,
        timestamp: Timestamp.fromDate(record.timestamp)
      });
    } catch (error) {
      Logger.error('Failed to store uptime record in Firestore', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Calculate SLA metrics for a period
   */
  public async calculateSLAMetrics(startDate: Date, endDate: Date): Promise<SLAMetrics> {
    try {
      // Skip Firestore during prerender/SSR, return default metrics
      if (typeof window === 'undefined' || typeof navigator === 'undefined' || 
          /HeadlessChrome/i.test(navigator?.userAgent || '')) {
        const periodDuration = endDate.getTime() - startDate.getTime();
        return {
          targetUptime: this.config.targetUptime,
          currentUptime: 100,
          totalTime: periodDuration,
          uptime: periodDuration,
          downtime: 0,
          degradedTime: 0,
          incidents: 0,
          mttr: 0,
          complianceStatus: 'compliant',
          periodStart: startDate,
          periodEnd: endDate
        };
      }

      const db = getFailoverAwareDatabase();
      if (!db) {
        Logger.warn('Database not available for SLA metrics calculation');
        const periodDuration = endDate.getTime() - startDate.getTime();
        return {
          targetUptime: this.config.targetUptime,
          currentUptime: 100,
          totalTime: periodDuration,
          uptime: periodDuration,
          downtime: 0,
          degradedTime: 0,
          incidents: 0,
          mttr: 0,
          complianceStatus: 'compliant',
          periodStart: startDate,
          periodEnd: endDate
        };
      }
      const q = query(
        collection(db, this.config.collectionName),
        where('timestamp', '>=', Timestamp.fromDate(startDate)),
        where('timestamp', '<=', Timestamp.fromDate(endDate)),
        orderBy('timestamp', 'asc')
      );

      const snapshot = await getDocs(q);
      const records: UptimeRecord[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date()
        } as UptimeRecord;
      });

      // Calculate metrics
      let totalTime = 0;
      let uptime = 0;
      let downtime = 0;
      let degradedTime = 0;
      let incidents = 0;
      let lastIncident: Date | undefined;
      let incidentDurations: number[] = [];
      let currentIncidentStart: number | null = null;

      records.forEach(record => {
        totalTime += record.duration;

        switch (record.status) {
          case 'up':
            uptime += record.duration;
            if (currentIncidentStart !== null) {
              // Incident ended
              incidentDurations.push(record.timestamp.getTime() - currentIncidentStart);
              currentIncidentStart = null;
            }
            break;
          case 'down':
            downtime += record.duration;
            if (currentIncidentStart === null) {
              // Incident started
              incidents++;
              currentIncidentStart = record.timestamp.getTime();
              lastIncident = record.timestamp;
            }
            break;
          case 'degraded':
            degradedTime += record.duration;
            break;
        }
      });

      // Calculate MTTR (Mean Time To Recovery)
      const mttr = incidentDurations.length > 0
        ? incidentDurations.reduce((a, b) => a + b, 0) / incidentDurations.length
        : 0;

      // Calculate current uptime percentage
      const currentUptime = totalTime > 0 ? (uptime / totalTime) * 100 : 100;

      // Determine compliance status
      let complianceStatus: 'compliant' | 'at_risk' | 'non_compliant';
      if (currentUptime >= this.config.targetUptime) {
        complianceStatus = 'compliant';
      } else if (currentUptime >= this.config.alertThreshold) {
        complianceStatus = 'at_risk';
      } else {
        complianceStatus = 'non_compliant';
      }

      return {
        targetUptime: this.config.targetUptime,
        currentUptime,
        totalTime,
        uptime,
        downtime,
        degradedTime,
        incidents,
        mttr,
        lastIncident,
        complianceStatus,
        periodStart: startDate,
        periodEnd: endDate
      };
    } catch (error) {
      Logger.error('Failed to calculate SLA metrics', error instanceof Error ? error : new Error('Unknown error'));
      // Return default metrics on error
      const periodDuration = endDate.getTime() - startDate.getTime();
      return {
        targetUptime: this.config.targetUptime,
        currentUptime: 100,
        totalTime: periodDuration,
        uptime: periodDuration,
        downtime: 0,
        degradedTime: 0,
        incidents: 0,
        mttr: 0,
        complianceStatus: 'compliant',
        periodStart: startDate,
        periodEnd: endDate
      };
    }
  }

  /**
   * Get current uptime status
   */
  public getCurrentStatus(): 'up' | 'down' | 'degraded' {
    return this.currentStatus;
  }

  /**
   * Get recent uptime records
   */
  public getRecentRecords(limit: number = 100): UptimeRecord[] {
    return this.uptimeRecords.slice(-limit);
  }

  /**
   * Get configuration
   */
  public getConfig(): Readonly<SLAMonitoringConfig> {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<SLAMonitoringConfig>): void {
    // Skip during prerender/SSR (Firebase not initialized)
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || 
        /HeadlessChrome/i.test(navigator?.userAgent || '')) {
      Logger.info('Uptime SLA Monitoring skipped during prerender');
      return;
    }

    this.config = { ...this.config, ...config };

    if (this.config.enabled && !this.isMonitoring) {
      this.startMonitoring();
    } else if (!this.config.enabled && this.isMonitoring) {
      this.stopMonitoring();
    }

    Logger.info('Uptime SLA Monitoring configuration updated', this.config);
  }

  /**
   * Stop monitoring
   */
  public stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    this.isMonitoring = false;
    Logger.info('Uptime SLA Monitoring stopped');
  }
}

// Export singleton instance
const uptimeSLAMonitoringService = UptimeSLAMonitoringService.getInstance();

export default uptimeSLAMonitoringService;

